import { FC, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { CatalogPlacement, CatalogScene, CatalogSort, CreditPack } from '../ApiTypes';
import { createSwapFromScene, getCreditPacks, getSceneCatalog, SceneNotFoundError, SourceVideoUnavailableError } from '../api';
import { getSceneThumbnailUrl } from '../Storage';
import { useUserStateContext } from '../contexts/UserStateContext';
import { FailureMessage } from '../styles/Messages';
import CreditPackOffer from './CreditPackOffer';

// サムネイル生成が終わるまでの待ち時間。
// 生成は数秒で終わるので最初は短く見に行き、待たされるほど間隔を広げる
// （固定 3 秒だと、出来ているのに最大 3 秒待たせてしまう）。
const POLLING_INTERVALS_MS = [800, 1000, 1500, 2000, 3000];
// 履歴からシーンを作るところから始まるユーザー（カタログを初めて開く人）は
// 棚が埋まるまで 1 分以上かかる。開いている間は追い続ける。
const MAX_POLLING_COUNT = 60;

// シーン一覧を持っていない初回だけ出す骨組み。棚の件数（最大 48）ぶん並べると
// 空枠だらけになるので、1 行分に抑える。
const SKELETON_COUNT = 4;

// 生成待ちのタイルを並べる上限。棚が空のときに画面いっぱいが「準備中」の枠で
// 埋まると読み込み中に見えてしまうので、揃っているタイルを見せることを優先する。
const MAX_PENDING_TILES = 4;

const getPollingInterval = (count: number) =>
  POLLING_INTERVALS_MS[Math.min(count, POLLING_INTERVALS_MS.length - 1)];

// シーン一覧は全ユーザー共通で、顔を変えても変わらない。**localStorage** に
// 残しておくことで、再訪時もサーバーの応答を待たずにタイルを描画できる
// （応答が必要なのは `ready` の確定だけ）。
const SCENES_CACHE_KEY = "catalogScenes";

type CachedScene = { sceneId: string, title: string };

const readCachedScenes = (sort: CatalogSort): CachedScene[]|null => {
  try {
    const raw = localStorage.getItem(`${SCENES_CACHE_KEY}:${sort}`);
    const cached = raw ? JSON.parse(raw) as CachedScene[] : null;
    return cached && cached.length > 0 ? cached : null;
  } catch {
    return null;
  }
};

const writeCachedScenes = (sort: CatalogSort, scenes: CatalogScene[]) => {
  try {
    const cached: CachedScene[] = scenes.map(scene => ({ sceneId: scene.sceneId, title: scene.title }));
    localStorage.setItem(`${SCENES_CACHE_KEY}:${sort}`, JSON.stringify(cached));
  } catch {
    // 保存できなくても表示には影響しない。
  }
};

// まとめ買いの提案はセッション中 1 回だけ。毎回出すと、常連ほど 1 タップで
// 買えなくなって逆効果になる。
const PACK_OFFERED_KEY = "catalogPackOffered";

const hasOfferedPack = () => sessionStorage.getItem(PACK_OFFERED_KEY) == "1";
const markPackOffered = () => sessionStorage.setItem(PACK_OFFERED_KEY, "1");

const Container = styled.div`
  margin-top: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #f0f0f0;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #8a8a8a;
`;

const SortTabs = styled.div`
  display: flex;
  gap: 8px;
  margin: 14px 0px;
`;

const SortTab = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${props => props.$active ? 'rgb(254, 167, 0)' : '#3a3a3a'};
  background-color: ${props => props.$active ? 'rgba(254, 167, 0, 0.15)' : 'transparent'};
  color: ${props => props.$active ? 'rgb(254, 167, 0)' : '#a8a8a8'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  margin-top: 14px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const Tile = styled.button`
  position: relative;
  padding: 0px;
  border: 1px solid #333333;
  border-radius: 12px;
  background-color: #262626;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    border-color: rgb(254, 167, 0);
    transform: translateY(-2px);
  }

  &:disabled {
    cursor: default;
  }

  @media (hover: none) {
    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

const TileImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
`;

const Shimmer = keyframes`
  100% { background-position-x: -200%; }
`;

const TilePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(100deg, #2f2f2f 40%, #3a3a3a 50%, #2f2f2f 60%) right / 200% 100%;
  animation: ${Shimmer} 1.4s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8a8a8a;
  font-size: 12px;
`;

const TileTitle = styled.div`
  padding: 8px 10px 10px 10px;
  font-size: 13px;
  line-height: 1.4;
  color: #e0e0e0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
`;

const PriceTag = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: rgba(0, 0, 0, 0.78);
  color: rgb(254, 167, 0);
  font-size: 11px;
  font-weight: 700;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(254, 167, 0);
  font-size: 13px;
  font-weight: 700;
`;

const PendingNote = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: #8a8a8a;
`;

// 棚はそのユーザー自身が使ってきた動画で組み立てるので、並び順も
// 「自分がよく使う順」と「最近使った順」になる。
const SORTS: { sort: CatalogSort, label: JSX.Element }[] = [
  { sort: "used", label: <Trans>Most used</Trans> },
  { sort: "recent", label: <Trans>Recent</Trans> },
];

/**
 * 顔交換済みサムネイルのグリッド。
 *
 * タイル 1 枚 1 枚が「その顔で、その状況」として既に存在してしまっているので、
 * 選ぶ行為がそのまま購入衝動になる。静止画なので快感は先渡ししない。
 */
const SceneCatalog: FC<{
  faceId: string,
  placement: CatalogPlacement,
  excludeSceneId?: string,
  showSortTabs?: boolean,
  limit?: number
}> = (props) => {
  const navigate = useNavigate();
  const userId = useUserStateContext().userId;
  const [scenes, setScenes] = useState<CatalogScene[]|null>(null);
  const [sort, setSort] = useState<CatalogSort>("used");
  // 未確定のタイル。サーバーの応答前でも画像を出しておき、無ければ 404 で
  // プレースホルダに落とす。顔を切り替えた瞬間に表示を差し替えるための仕組み。
  const [optimistic, setOptimistic] = useState<CachedScene[]|null>(() => readCachedScenes("used"));
  const [missingImages, setMissingImages] = useState<Set<string>>(new Set());
  const [purchasingSceneId, setPurchasingSceneId] = useState<string|null>(null);
  const [failure, setFailure] = useState<"unavailable"|"unknown"|null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [offeredSceneId, setOfferedSceneId] = useState<string|null>(null);
  const pollingCount = useRef(0);

  const hasScenes = scenes != null && scenes.length > 0;

  // パックはタイルが出ている場合しか使わないので、棚が空のうちは問い合わせない。
  useEffect(() => {
    if (!hasScenes) {
      return;
    }
    getCreditPacks().then(setPacks).catch(() => setPacks([]));
  }, [hasScenes]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>|undefined;
    let canceled = false;
    pollingCount.current = 0;

    // 顔や並び順が変わったら、確定済みの表示は捨てて即座に楽観表示に切り替える。
    // シーン一覧は共通なので、URL の顔部分だけが変わって瞬時に差し替わる。
    setScenes(null);
    setMissingImages(new Set());
    setOptimistic(readCachedScenes(sort));

    const load = async (reportView: boolean) => {
      try {
        const result = await getSceneCatalog({
          faceId: props.faceId,
          placement: props.placement,
          sort,
          limit: props.limit,
          excludeSceneId: props.excludeSceneId,
          reportView
        });
        if (canceled) {
          return;
        }
        setScenes(result.scenes);
        writeCachedScenes(sort, result.scenes);
        setHasPurchased(result.hasPurchased);
        // 生成待ちのタイルが残っている間だけ、揃うまで見に行く。
        if (result.pendingCount > 0 && pollingCount.current < MAX_POLLING_COUNT) {
          const interval = getPollingInterval(pollingCount.current);
          pollingCount.current += 1;
          timer = setTimeout(() => load(false), interval);
        }
      } catch (exception) {
        if (!canceled) {
          setScenes([]);
        }
      }
    };
    load(true);

    return () => {
      canceled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [props.faceId, props.placement, props.excludeSceneId, props.limit, sort]);

  const purchase = async (sceneId: string) => {
    setOfferedSceneId(null);
    setPurchasingSceneId(sceneId);
    setFailure(null);
    try {
      const result = await createSwapFromScene(sceneId, props.faceId, props.placement);
      if (result.actionUrl) {
        window.location.href = result.actionUrl;
      } else {
        navigate(`/swaps/${result.swapId}?generating=1`);
      }
    } catch (exception) {
      setPurchasingSceneId(null);
      if (exception instanceof SceneNotFoundError || exception instanceof SourceVideoUnavailableError) {
        setScenes(current => (current ?? []).filter(scene => scene.sceneId != sceneId));
        setFailure("unavailable");
      } else {
        setFailure("unknown");
      }
    }
  };

  const selectScene = (sceneId: string) => {
    // 既に買ったことがある人が次のタイルを選んだ瞬間は「もっと欲しい」を行動で
    // 示した直後。まとめ買いを提案する意味があるのはこのタイミングだけなので、
    // セッション中 1 回に限って挟む。初回の人は 1 タップのまま。
    if (packs.length > 0 && hasPurchased && !hasOfferedPack()) {
      markPackOffered();
      setOfferedSceneId(sceneId);
      return;
    }
    purchase(sceneId);
  };

  if (scenes != null && scenes.length == 0) {
    return <></>;
  }

  // 表示するタイル。サーバーの応答が来るまでは保持しているシーン一覧で描き、
  // 画像は (userId, faceId, sceneId) から決まる URL を直接指す。
  // ready が undefined = 未確定（画像を出してみて、無ければ 404 で落とす）。
  const allTiles: { sceneId: string, title: string, ready?: boolean }[] =
    scenes != null
      ? scenes.map(scene => ({ sceneId: scene.sceneId, title: scene.title, ready: scene.ready }))
      : (optimistic ?? []).map(scene => ({ sceneId: scene.sceneId, title: scene.title }));

  const hasImage = (tile: { sceneId: string, ready?: boolean }) =>
    tile.ready !== false && !missingImages.has(tile.sceneId);

  // 揃っているタイルは並び順のまま出し、生成待ちは末尾に少しだけ添える。
  // 画面いっぱいが「準備中」の枠になると読み込み中に見えてしまう。
  const tiles = [
    ...allTiles.filter(hasImage),
    ...allTiles.filter(tile => !hasImage(tile)).slice(0, MAX_PENDING_TILES),
  ];
  const hiddenPendingCount = allTiles.filter(tile => !hasImage(tile)).length - MAX_PENDING_TILES;

  return <Container>
    <Header>
      <Title><Trans>More with this face</Trans></Title>
      <Subtitle><Trans>Pick a scene. Her face is already in it.</Trans></Subtitle>
    </Header>

    {props.showSortTabs && <SortTabs>
      {SORTS.map(item => <SortTab key={item.sort} $active={sort == item.sort} onClick={() => setSort(item.sort)}>
        {item.label}
      </SortTab>)}
    </SortTabs>}

    {failure == "unavailable" && <FailureMessage>
      <Trans>This video is currently unavailable. Please try another video.</Trans>
    </FailureMessage>}
    {failure == "unknown" && <FailureMessage>
      <Trans>Failed to start generation. Please try again.</Trans>
    </FailureMessage>}

    <Grid>
      {(tiles.length > 0 ? tiles : new Array(SKELETON_COUNT).fill(null)).map((tile, index: number) => {
        if (tile == null) {
          return <Tile key={`skeleton-${index}`} disabled><TilePlaceholder /></Tile>;
        }
        const showImage = hasImage(tile);
        return <Tile key={tile.sceneId} disabled={!showImage || purchasingSceneId != null} onClick={() => selectScene(tile.sceneId)}>
          {showImage
            ? <TileImage src={userId ? getSceneThumbnailUrl(userId, props.faceId, tile.sceneId) : ""}
                         alt={tile.title} loading="lazy"
                         onError={() => setMissingImages(current => new Set(current).add(tile.sceneId))} />
            : <TilePlaceholder><Trans>Preparing</Trans></TilePlaceholder>}
          {showImage && <PriceTag>$9.99</PriceTag>}
          {purchasingSceneId == tile.sceneId && <LoadingOverlay><Trans>Loading...</Trans></LoadingOverlay>}
          <TileTitle>{tile.title}</TileTitle>
        </Tile>;
      })}
    </Grid>

    {hiddenPendingCount > 0 && <PendingNote>
      <Trans>Preparing</Trans> ({hiddenPendingCount})
    </PendingNote>}

    {offeredSceneId && <CreditPackOffer
      packs={packs}
      onBuySingle={() => purchase(offeredSceneId)}
      onClose={() => setOfferedSceneId(null)} />}
  </Container>;
};

export default SceneCatalog;
